// Scope-aware broadcast detail. One view, four call sites:
//   • /admin/broadcasts/:id                                (platform admin)
//   • /circles/:id/admin/broadcasts/:broadcastId           (circle leaders + admins)
//   • /networks/:id/admin/broadcasts/:broadcastId          (network leaders + admins)
//   • /groups/:id/admin/broadcasts/:broadcastId            (space leaders + admins)
//
// The caller picks the back link; the data fetch is scope-aware so the
// server-side rule predicates apply (group-leader scope can only see their
// own group's broadcasts).

import { useEffect, useState, useTransition } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  Body,
  Button,
  Card,
  Dialog,
  DialogFooter,
  DialogSection,
  EyebrowField,
  Field,
  Heading,
  I,
  Input,
  PageHeader,
  Row,
  Stack,
  Stat,
} from '@leanin/ui';

import {
  BroadcastStatusPill,
  isBroadcastSending,
} from '@/components/admin/BroadcastStatusPill';
import { ErrorState } from '@leanin/ui';
import { LoadingState } from '@/components/states/index';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { isoToDatetimeLocalInput } from '@/lib/format-date';
import { useFormatDate } from '@/lib/format-date.client';
import {
  useBroadcastDetail,
  type BroadcastScope,
} from '@/lib/queries/broadcasts';
import {
  useDeleteBroadcast,
  usePreviewBroadcast,
  useRescheduleBroadcast,
} from '@/lib/mutations/admin';
import { useStepUp } from '@/lib/use-step-up';

type Props = {
  readonly scope: BroadcastScope;
  readonly broadcastId: string;
};

// Spec kind → human-readable label. The composer uses the same vocabulary
// in its picker; we mirror it here so the read-of-record matches the create
// flow's language.
const AUDIENCE_LABELS: Record<string, string> = {
  all: 'All members',
  circles: 'Specific circles',
  networks: 'Specific networks',
  spaces: 'Specific spaces',
  members: 'Specific members',
  role: 'By role',
  recent_active: 'Recently active',
};

// Send-via enum → label. Matches the composer's send-method picker.
const SEND_VIA_LABELS: Record<string, string> = {
  email: 'Email',
  email_in_app: 'Email + in-app',
};

function labelFor(map: Record<string, string>, key: string): string {
  return map[key] ?? key;
}

// Display label for a broadcast's recipient picker. Every row carries a
// non-null `audienceSpec` post-Phase 3; the label maps the spec kind.
function audienceLabel(view: { audienceSpec?: unknown }): string {
  const spec = view.audienceSpec as { kind?: unknown } | null;
  const kind = spec?.kind;
  return typeof kind === 'string' ? labelFor(AUDIENCE_LABELS, kind) : 'Custom';
}

// Detail URL → edit URL by scope. We reconstruct from scope + broadcastId,
// matching the path patterns owned by the four route files under
// `apps/web/src/routes/_authed/.../broadcasts/`.
function editHrefFor(scope: BroadcastScope, broadcastId: string): string {
  if (scope.kind === 'platform') return `/admin/broadcasts/${broadcastId}/edit`;
  if (scope.groupKind === 'circle') {
    return `/circles/${scope.groupId}/admin/broadcasts/${broadcastId}/edit`;
  }
  if (scope.groupKind === 'network') {
    return `/networks/${scope.groupId}/admin/broadcasts/${broadcastId}/edit`;
  }
  return `/groups/${scope.groupId}/admin/broadcasts/${broadcastId}/edit`;
}

// List URL by scope — used as the post-delete navigation target. Same shape
// as `editHrefFor`, pointed at the admin list section instead of the edit form.
function listHrefFor(scope: BroadcastScope): string {
  if (scope.kind === 'platform') return '/admin/broadcasts';
  if (scope.groupKind === 'circle') {
    return `/circles/${scope.groupId}/admin?section=broadcasts`;
  }
  if (scope.groupKind === 'network') {
    return `/networks/${scope.groupId}/admin?section=broadcasts`;
  }
  return `/groups/${scope.groupId}/admin?section=broadcasts`;
}

function percent(numerator: number, denominator: number | null): string {
  if (denominator === null || denominator <= 0) return '—';
  const v = (numerator / denominator) * 100;
  // One decimal place keeps small samples honest without looking spurious for
  // large sends. Locale-aware so the comma/period matches the rest of the UI.
  return `${v.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}

export function BroadcastDetailView({ scope, broadcastId }: Props) {
  const fmt = useFormatDate();
  const q = useBroadcastDetail(scope, broadcastId);
  const navigate = useNavigate();
  const del = useDeleteBroadcast();
  const reschedule = useRescheduleBroadcast();
  const { runStepUp } = useStepUp();
  // Body is rendered through the same code path the recipient sees:
  // POST /broadcasts/{id}/preview returns the wrapped HTML (merge tags
  // already substituted with the viewer's first name, server-side). We
  // fire it once on mount and pipe the returned full HTML document into a
  // sandboxed iframe via `srcDoc` — the same surface a recipient sees in
  // their inbox, with the email's own DOCTYPE + <head> + inline <style>
  // intact. The previous `<div dangerouslySetInnerHTML>` approach nested
  // a full HTML document inside a div, which browsers reject and fall
  // back to default fonts on — that's the "broken fonts" bug.
  const preview = usePreviewBroadcast();
  const { mutate: runPreview } = preview;
  useEffect(() => {
    runPreview({ id: broadcastId });
  }, [broadcastId, runPreview]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [pending, start] = useTransition();
  if (isNotFoundApiError(q.error)) return <NotFoundPage />;
  if (q.isError) {
    return (
      <ErrorState
        title="We couldn't load that broadcast."
        body="Try again in a moment."
      />
    );
  }
  const view = q.data;
  if (view === undefined) return null;

  // Destructive action is platform-admin only — the DELETE endpoint is
  // gated on admin role, so the button only renders when we know it'll
  // succeed. Scheduled rows can still be cancelled (status='scheduled');
  // sent rows are immutable; drafts are no longer destructible from this
  // view (use the Edit flow to finish, or wait for archive support).
  const canCancelScheduled =
    scope.kind === 'platform' && view.status === 'scheduled';
  const dialogTitle = `Cancel the scheduled send of '${view.subject}'?`;
  const dialogBody =
    'The broadcast will not be sent. This removes the record.';

  const isSent = view.status === 'sent';
  const isDraft = view.status === 'draft';
  const isScheduled = view.status === 'scheduled';
  // A scheduled broadcast whose send time has passed is mid/awaiting dispatch
  // — surfaced as "Sending" (same rule the list + pill use) so every status
  // surface on this page agrees.
  const isSending = isBroadcastSending(view.status, view.scheduledSendAt);
  const editHref = editHrefFor(scope, broadcastId);
  const recipientCount = view.recipientCount ?? 0;

  // Secondary line under the page title: status pill · recipients · timing.
  // Compact factoid row so the most-asked questions ("did this go out?",
  // "to how many people?", "when?") answer themselves at a glance.
  const headerMeta = (
    <Row gap="md" align="center" wrap>
      <BroadcastStatusPill
        status={view.status}
        scheduledSendAt={view.scheduledSendAt}
      />
      <Body size="sm" color="mute" as="span">
        {recipientCount.toLocaleString('en-US')}{' '}
        {recipientCount === 1 ? 'recipient' : 'recipients'}
      </Body>
      <Body size="sm" color="mute" as="span">
        {isSent && view.sentAt
          ? `Sent ${fmt.medium(view.sentAt)}`
          : isSending
            ? 'Sending now'
            : isScheduled && view.scheduledSendAt
              ? `Scheduled for ${fmt.medium(view.scheduledSendAt)}`
              : `Draft · created ${fmt.medium(view.createdAt)}`}
      </Body>
    </Row>
  );

  // Header-right actions. The status + timing already live in the header
  // (headerMeta), so we surface only the actionable affordances here rather
  // than in a separate tinted callout band.
  const headerActions = isDraft ? (
    <Button variant="primary" size="md" href={editHref}>
      Edit draft
    </Button>
  ) : canCancelScheduled ? (
    <Row gap="sm" align="center">
      <Button
        variant="secondary"
        size="md"
        disabled={pending || reschedule.isPending}
        onClick={() => {
          setRescheduleAt(
            view.scheduledSendAt
              ? isoToDatetimeLocalInput(view.scheduledSendAt)
              : '',
          );
          setRescheduleError(null);
          setRescheduleOpen(true);
        }}
      >
        Reschedule
      </Button>
      <Button
        variant="secondary"
        size="md"
        disabled={pending || reschedule.isPending}
        onClick={() => setConfirmOpen(true)}
      >
        Cancel send
      </Button>
    </Row>
  ) : undefined;

  return (
    <Stack gap="xl">
      <PageHeader
        title={view.subject}
        preview={headerMeta}
        {...(headerActions !== undefined ? { actions: headerActions } : {})}
      />

      {isSent ? (
        <Stack gap="md">
          {/* Primary metrics — counts users acted on. Open count is included
           * but should be read with the caveat below: Apple Mail Privacy
           * Protection pre-fetches the tracking pixel for every recipient,
           * which inflates this number into noise for Apple Mail users
           * (~50% of opens industry-wide). Clicks and unsubscribes are the
           * honest engagement signals. */}
          <Row gap="md" wrap>
            <Stat
              label="Recipients"
              value={recipientCount.toLocaleString('en-US')}
            />
            <Stat
              label="Clicks"
              value={view.clickCount.toLocaleString('en-US')}
            />
            <Stat
              label="Unsubscribes"
              value={view.metrics.unsubscribedCount.toLocaleString('en-US')}
            />
            <Stat
              label="Opens"
              value={view.openCount.toLocaleString('en-US')}
            />
          </Row>
          {/* Secondary: rates. Smaller, muted typography — these are derived
           * percentages, not primary metrics. Click rate is the campaign
           * KPI; open rate is shown for completeness but post-APMP is
           * largely noise. */}
          <Row gap="lg" wrap>
            <Body size="sm" color="mute">
              Click rate:{' '}
              <strong>
                {percent(view.clickCount, view.recipientCount)}
              </strong>
            </Body>
            <Body size="sm" color="mute">
              Unsubscribe rate:{' '}
              <strong>
                {percent(
                  view.metrics.unsubscribedCount,
                  view.recipientCount,
                )}
              </strong>
            </Body>
            <Body size="sm" color="mute">
              Open rate (indicative — APMP-inflated):{' '}
              <strong>
                {percent(view.openCount, view.recipientCount)}
              </strong>
            </Body>
          </Row>
        </Stack>
      ) : null}

      {preview.isError ? (
        <Card pad="lg">
          <ErrorState
            bordered={false}
            title="We couldn't render this email."
            body="Try again in a moment."
          />
        </Card>
      ) : preview.data ? (
        <Card pad="none" style={{ overflow: 'hidden' }}>
          <iframe
            title="Email body preview"
            srcDoc={preview.data.html}
            sandbox=""
            style={{
              width: '100%',
              minHeight: 600,
              border: 'none',
              background: 'var(--background)',
              display: 'block',
            }}
          />
        </Card>
      ) : (
        <Card pad="lg">
          <LoadingState rows={1} label="Rendering email" />
        </Card>
      )}

      <Card pad="lg">
        <Stack gap="lg">
          <Heading level={3}>Details</Heading>
          <Row gap="xl" wrap>
            <EyebrowField label="Subject">
              <Body size="md">{view.subject}</Body>
            </EyebrowField>
            <EyebrowField label="Audience">
              <Body size="md">{audienceLabel(view)}</Body>
            </EyebrowField>
            <EyebrowField label="Send via">
              <Body size="md">{labelFor(SEND_VIA_LABELS, view.sendVia)}</Body>
            </EyebrowField>
            <EyebrowField label="Recipients">
              <Body size="md">{recipientCount.toLocaleString('en-US')}</Body>
            </EyebrowField>
            {view.preheaderText !== null ? (
              <EyebrowField label="Preheader">
                <Body size="md">{view.preheaderText}</Body>
              </EyebrowField>
            ) : null}
            <EyebrowField label="Reply-to">
              <Body size="md">{view.replyTo ?? 'Default reply-to'}</Body>
            </EyebrowField>
            <EyebrowField label="Version">
              <Body size="md">v.{view.version}</Body>
            </EyebrowField>
            <EyebrowField label="Created">
              <Body size="md">{fmt.medium(view.createdAt)}</Body>
            </EyebrowField>
          </Row>
        </Stack>
      </Card>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={dialogTitle}
        description={dialogBody}
        actionLabel="Cancel send"
        cancelLabel="Keep"
        destructive
        onAction={() => {
          start(async () => {
            await del.mutateAsync(broadcastId);
            void navigate({ to: listHrefFor(scope) });
          });
        }}
      />

      <Dialog
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        title="Reschedule send"
        description="Pick a new send time. Members will receive the broadcast at the chosen time instead."
        size="sm"
      >
        <DialogSection gap="md">
          <Field label="Date and time" required>
            <Input
              type="datetime-local"
              value={rescheduleAt}
              onChange={(e) => {
                setRescheduleAt(e.target.value);
                setRescheduleError(null);
              }}
              autoFocus
            />
          </Field>
          {rescheduleError !== null ? (
            <Body size="sm" color="destructive">
              {rescheduleError}
            </Body>
          ) : null}
        </DialogSection>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setRescheduleOpen(false)}
            disabled={reschedule.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            iconRight={I.cal}
            loading={reschedule.isPending}
            disabled={reschedule.isPending}
            onClick={() => {
              if (rescheduleAt.length === 0) {
                setRescheduleError('Pick a date and time.');
                return;
              }
              const target = new Date(rescheduleAt);
              if (Number.isNaN(target.getTime())) {
                setRescheduleError('Invalid date.');
                return;
              }
              if (target.getTime() <= Date.now()) {
                setRescheduleError('Scheduled time must be in the future.');
                return;
              }
              setRescheduleError(null);
              // Rescheduling a platform send is step-up'd server-side (RBAC
              // Decision C); runStepUp opens Clerk's reverification modal and
              // retries once on success.
              void (async () => {
                const outcome = await runStepUp(
                  () =>
                    reschedule.mutateAsync({
                      id: broadcastId,
                      scheduledAt: target.toISOString(),
                    }),
                  'Reschedule failed.',
                );
                if (outcome.status === 'ok') {
                  setRescheduleOpen(false);
                  return;
                }
                if (outcome.status === 'cancelled') return;
                setRescheduleError(outcome.message);
              })();
            }}
          >
            Save new time
          </Button>
        </DialogFooter>
      </Dialog>
    </Stack>
  );
}

